import { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, PenLine, Wind, Lightbulb, Heart } from "lucide-react";

const EXERCISE_ICONS = {
  reflection: PenLine,
  eq_scale: Lightbulb,
  journaling: PenLine,
  eq_checkin: Heart,
  action_plan: Lightbulb,
  breathing: Wind,
  eq_mapping: Heart,
};

const ExercisePanel = ({ lesson, progress, onComplete }) => {
  const [responses, setResponses] = useState({});
  const [scaleValues, setScaleValues] = useState({});
  const [breathingDone, setBreathingDone] = useState(false);

  const exercise = lesson?.exercise;
  const alreadyCompleted = progress?.exercise_completed;
  const Icon = EXERCISE_ICONS[exercise?.type] || PenLine;

  if (!exercise) return null;

  if (alreadyCompleted) {
    return (
      <div data-testid="exercise-completed" className="rounded-2xl p-8 text-center" style={{ background: "white", border: "1px solid var(--ll-border)" }}>
        <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4" style={{ background: "var(--ll-green)" }}>
          <CheckCircle size={28} style={{ color: "white" }} />
        </div>
        <h3 className="font-serif text-xl font-medium mb-2" style={{ color: "var(--ll-text)" }}>Exercise Completed</h3>
        <p className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
          Well done! You've completed the {exercise.title} exercise.
        </p>
      </div>
    );
  }

  const handleComplete = () => {
    const allResponses = { ...responses, ...scaleValues, breathingDone };
    onComplete(JSON.stringify(allResponses));
  };

  const renderExercise = () => {
    switch (exercise.type) {
      case "reflection":
        return (
          <div className="space-y-4">
            <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
              {exercise.prompt}
            </p>
            <Textarea data-testid="exercise-reflection-input" placeholder="Write your reflections here..." rows={6}
              value={responses.reflection || ""} onChange={(e) => setResponses({ ...responses, reflection: e.target.value })}
              className="rounded-xl border bg-white/50 focus:bg-white resize-none" style={{ borderColor: "var(--ll-border)" }} />
          </div>
        );

      case "eq_scale":
        return (
          <div className="space-y-4">
            <p className="text-sm leading-relaxed" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>{exercise.prompt}</p>
            {exercise.scales?.map((scale, i) => (
              <div key={i} className="p-5 rounded-xl" style={{ background: "var(--ll-bg-secondary)" }}>
                <p className="text-sm font-medium mb-1" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>{scale.label}</p>
                <p className="text-xs mb-4" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>{scale.description}</p>
                <div className="flex items-center gap-2">
                  <span className="text-[10px]" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Low</span>
                  <div className="flex gap-1 flex-1 justify-center">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                      <button key={n} data-testid={`scale-${i}-${n}`} onClick={() => setScaleValues({ ...scaleValues, [scale.label]: n })}
                        className="w-8 h-8 rounded-lg text-xs font-medium transition-all duration-200"
                        style={{
                          background: scaleValues[scale.label] === n ? "var(--ll-cta)" : "white",
                          color: scaleValues[scale.label] === n ? "var(--ll-cta-text)" : "var(--ll-text-muted)",
                          border: `1px solid ${scaleValues[scale.label] === n ? "var(--ll-cta)" : "var(--ll-border)"}`,
                          fontFamily: "'Outfit', sans-serif",
                        }}>
                        {n}
                      </button>
                    ))}
                  </div>
                  <span className="text-[10px]" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>High</span>
                </div>
              </div>
            ))}
          </div>
        );

      case "journaling":
        return (
          <div className="space-y-4">
            {exercise.prompts?.map((prompt, i) => (
              <div key={i} className="space-y-2">
                <p className="text-sm font-medium flex items-start gap-2" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>
                  <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-[10px]"
                    style={{ background: "var(--ll-accent)", color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>{i + 1}</span>
                  {prompt}
                </p>
                <Textarea data-testid={`journal-prompt-${i}`} placeholder="Your thoughts..." rows={3}
                  value={responses[`prompt_${i}`] || ""} onChange={(e) => setResponses({ ...responses, [`prompt_${i}`]: e.target.value })}
                  className="rounded-xl border bg-white/50 focus:bg-white resize-none ml-7" style={{ borderColor: "var(--ll-border)" }} />
              </div>
            ))}
          </div>
        );

      case "eq_checkin":
        return (
          <div className="space-y-4">
            <p className="text-sm leading-relaxed" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>{exercise.prompt}</p>
            {exercise.questions?.map((q, i) => (
              <div key={i} className="p-4 rounded-xl" style={{ background: "var(--ll-bg-secondary)" }}>
                <p className="text-sm font-medium mb-2" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>{q}</p>
                <Textarea data-testid={`checkin-${i}`} placeholder="Be honest with yourself..." rows={2}
                  value={responses[`checkin_${i}`] || ""} onChange={(e) => setResponses({ ...responses, [`checkin_${i}`]: e.target.value })}
                  className="rounded-xl border bg-white/50 focus:bg-white resize-none" style={{ borderColor: "var(--ll-border)" }} />
              </div>
            ))}
            {exercise.note && (
              <div className="p-3 rounded-xl text-xs" style={{ background: "rgba(247,208,70,0.08)", color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
                {exercise.note}
              </div>
            )}
          </div>
        );

      case "breathing":
        return (
          <div className="space-y-6">
            <div className="rounded-2xl p-8 text-center" style={{ background: "linear-gradient(135deg, rgba(163,184,153,0.1), rgba(247,208,70,0.08))" }}>
              <Wind size={40} className="mx-auto mb-4" style={{ color: "var(--ll-green)" }} />
              <h4 className="font-serif text-lg font-medium mb-4" style={{ color: "var(--ll-text)" }}>4-7-8 Breathing Exercise</h4>
              <div className="space-y-3 text-left max-w-sm mx-auto">
                {exercise.instructions?.map((step, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-medium"
                      style={{ background: "var(--ll-accent)", color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>{i + 1}</span>
                    <p className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>{step}</p>
                  </div>
                ))}
              </div>
              {!breathingDone && (
                <button data-testid="breathing-done-btn" onClick={() => setBreathingDone(true)}
                  className="mt-6 rounded-full px-6 py-2.5 text-xs font-medium transition-all duration-300 hover:-translate-y-0.5"
                  style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}>
                  I've Completed the Breathing Exercise
                </button>
              )}
              {breathingDone && <p className="mt-4 text-xs flex items-center justify-center gap-1" style={{ color: "var(--ll-green)", fontFamily: "'Outfit', sans-serif" }}><CheckCircle size={12} /> Breathing exercise done</p>}
            </div>
            {exercise.followup && breathingDone && (
              <div className="space-y-3">
                <p className="text-sm leading-relaxed" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>{exercise.followup}</p>
                <Textarea data-testid="breathing-followup" placeholder="Write your reframing here..." rows={4}
                  value={responses.followup || ""} onChange={(e) => setResponses({ ...responses, followup: e.target.value })}
                  className="rounded-xl border bg-white/50 focus:bg-white resize-none" style={{ borderColor: "var(--ll-border)" }} />
              </div>
            )}
          </div>
        );

      case "action_plan":
        return (
          <div className="space-y-4">
            {exercise.categories?.map((cat, i) => (
              <div key={i} className="p-5 rounded-xl" style={{ background: "var(--ll-bg-secondary)" }}>
                <p className="text-sm font-medium mb-1" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>{cat.name}</p>
                <p className="text-xs mb-3" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>{cat.prompt}</p>
                <Textarea data-testid={`action-${i}`} placeholder="Your commitment..." rows={2}
                  value={responses[`action_${i}`] || ""} onChange={(e) => setResponses({ ...responses, [`action_${i}`]: e.target.value })}
                  className="rounded-xl border bg-white/50 focus:bg-white resize-none" style={{ borderColor: "var(--ll-border)" }} />
              </div>
            ))}
          </div>
        );

      case "eq_mapping":
        return (
          <div className="space-y-4">
            <p className="text-sm leading-relaxed" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>{exercise.prompt}</p>
            <Textarea data-testid="eq-mapping-input" placeholder="Describe your support circles..." rows={5}
              value={responses.mapping || ""} onChange={(e) => setResponses({ ...responses, mapping: e.target.value })}
              className="rounded-xl border bg-white/50 focus:bg-white resize-none" style={{ borderColor: "var(--ll-border)" }} />
            {exercise.reflection_prompts?.map((rp, i) => (
              <div key={i} className="p-4 rounded-xl" style={{ background: "var(--ll-bg-secondary)" }}>
                <p className="text-sm font-medium mb-2" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>{rp}</p>
                <Textarea data-testid={`mapping-reflect-${i}`} placeholder="Your reflection..." rows={2}
                  value={responses[`reflect_${i}`] || ""} onChange={(e) => setResponses({ ...responses, [`reflect_${i}`]: e.target.value })}
                  className="rounded-xl border bg-white/50 focus:bg-white resize-none" style={{ borderColor: "var(--ll-border)" }} />
              </div>
            ))}
          </div>
        );

      default:
        return <p className="text-sm" style={{ color: "var(--ll-text-muted)" }}>Exercise type not supported.</p>;
    }
  };

  return (
    <div data-testid="exercise-panel" className="space-y-6">
      <div className="flex items-center gap-3">
        <Icon size={20} style={{ color: "var(--ll-green)" }} />
        <div>
          <h3 className="font-serif text-xl font-medium" style={{ color: "var(--ll-text)" }}>{exercise.title}</h3>
          <p className="text-xs" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
            Estimated time: {exercise.duration}
          </p>
        </div>
      </div>

      <div className="rounded-2xl p-6" style={{ background: "white", border: "1px solid var(--ll-border)" }}>
        {renderExercise()}
      </div>

      <button data-testid="exercise-complete-btn" onClick={handleComplete}
        className="w-full rounded-full px-6 py-3.5 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg flex items-center justify-center gap-2"
        style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}>
        <CheckCircle size={14} /> Complete Exercise
      </button>
    </div>
  );
};

export default ExercisePanel;
